/**
 * Bridges `store/theme.store.ts` (persisted user preference, STEP 9)
 * into a real MUI `Theme` (STEP 6) — re-resolves 'system' against the
 * OS media query live, so a user who picked "system" sees the app
 * follow an OS-level dark-mode toggle without a page reload.
 */
import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { useThemeStore } from '@store/theme.store';
import { usePreferencesStore, TEXT_SIZE_ATTRIBUTE } from '@store/preferences.store';
import { createAppTheme } from '@theme/index';

export function ThemeModeProvider({ children }: { children: ReactNode }): JSX.Element {
  const mode = useThemeStore((state) => state.mode);
  const textSize = usePreferencesStore((state) => state.textSize);
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);

  // The scale lives on <html> because it works by moving the ROOT font size,
  // which every `rem` in the app — Tailwind's and MUI's alike — resolves
  // against. Setting it lower down would scale that subtree's text while
  // leaving the boxes measured from the root behind.
  useEffect(() => {
    const attribute = TEXT_SIZE_ATTRIBUTE[textSize];
    if (attribute) document.documentElement.setAttribute('data-text-size', attribute);
    else document.documentElement.removeAttribute('data-text-size');
  }, [textSize]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent): void => setSystemPrefersDark(event.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  const resolvedMode = mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode;
  const theme = useMemo(() => createAppTheme(resolvedMode), [resolvedMode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}
